import { DialogModal } from "@common/components/alert/dialog/DialogModal"
import TextM from "@common/components/text/TextM"
import { useCamera } from "@common/hooks/useCamera"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { formIds } from "@data/constants/formConstants"
import { clearLastPhoto, clearPhotos } from "@data/redux/actions/camera"
import { documentPicker } from "@common/components/pdf/documentPicker"
import { checkIsPdf, isPdfFile } from "@common/components/pdf/pdfHelpers"
import * as ImagePicker from "expo-image-picker"
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import {
    ActivityIndicator,
    Button,
    Dialog,
    IconButton,
    Portal,
    TextInput,
} from "react-native-paper"
import {
    BackHandler,
    Platform,
    StyleSheet,
    TouchableOpacity,
    View,
} from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import { LinkPreview } from "@flyerhq/react-native-link-preview"
import { API_URL } from "@data/constants/apiConstants"
import { validation } from "@data/utility/validation"
import { MaterialImage } from "../../../student/material/MaterialImage"
import { MaterialPdf } from "../../../student/material/MaterialPdf"
import MaterialDisplayModal from "../../../student/activity/execute/MaterialDisplayModal"

/**
 * Reusable Media Picker Button Component
 *
 * Provides a button that opens a menu with options to:
 * - Add external link
 * - Take photo with camera
 * - Pick image from gallery (mobile only)
 * - Pick document (PDF/image)
 * 
 * See example usage in MaterialPicker.js and AddSchoolAssessmentModal.js

 */
const MediaPickerButton = ({
    images = [],
    setImages,
    pdf = null,
    setPdf,
    url = "",
    setUrl,
    onUrlTitleChange,
    attachments,
    multiple = false,
    buttonLabel = "Ajouter",
    addMoreButtonLabel = "Ajouter plus",
    showAddMoreButton = true,
    formId = formIds.NEW_MATERIAL,
    enableExternalLink = true,
    enableCamera = true,
    enableGallery = true,
    enableDocument = true,
    videoCourseMode = false,
    buttonStyle = {},
    addMoreButtonStyle = {},
    onMenuStateChange,
    lastPhoto,
    lastPhotos,
    lastPhotoFormId,
    clearLastPhoto,
    clearPhotos,
}) => {
    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()
    const [pickingImage, setPickingImage] = useState(false)
    const [urlDialogVisible, setUrlDialogVisible] = useState(false)
    const [urlInput, setUrlInput] = useState(url)
    const [urlError, setUrlError] = useState(null)
    const [isMaterialOpen, setMaterialOpen] = useState(false)
    const [confirmDeleteUrlModalVisible, setConfirmDeleteUrlModalVisible] =
        useState(false)
    const urlInputRef = useRef()
    const lastTitleRef = useRef(null)

    const [launchCamera] = useCamera(formId, true, false)

    const handleMenuOpen = () => {
        if (onMenuStateChange) onMenuStateChange(true)
    }

    const handleMenuClose = () => {
        if (onMenuStateChange) onMenuStateChange(false)
    }

    const handleMenuItemPress = (itemOnPress) => () => {
        handleMenuClose()
        itemOnPress()
    }

    const onShowMaterialModal = () => setMaterialOpen(!isMaterialOpen)

    // Sync urlInput with url prop
    useEffect(() => {
        setUrlInput(url)
    }, [url])

    // Reset lastTitleRef when URL changes
    useEffect(() => {
        lastTitleRef.current = null
    }, [url])

    // Handle photos from camera (Redux)
    useEffect(() => {
        if (
            lastPhotoFormId === formId &&
            !!lastPhoto &&
            setImages &&
            !images?.map((x) => x?.uri)?.includes(lastPhoto?.uri)
        ) {
            if (multiple) {
                // Multiple mode: add to array
                const imagesCopy = images?.map((x) => x) ?? []
                imagesCopy.push(lastPhoto)
                setImages(imagesCopy)
            } else {
                // Single mode: replace with single item
                setImages([lastPhoto])
            }
            clearLastPhoto()
        } else if (
            lastPhotoFormId === formId &&
            lastPhotos?.length > 0 &&
            setImages
        ) {
            if (multiple) {
                // Multiple mode: add all photos
                const imagesCopy = images?.map((x) => x) ?? []
                imagesCopy.push(...lastPhotos)
                setImages(imagesCopy)
            } else {
                // Single mode: keep only the first photo
                setImages([lastPhotos[0]])
            }
            clearPhotos()
        }
    }, [
        lastPhoto,
        lastPhotos,
        lastPhotoFormId,
        images,
        setImages,
        formId,
        multiple,
        clearLastPhoto,
        clearPhotos,
    ])

    const showUrlDialog = () => {
        setUrlInput(url)
        setUrlDialogVisible(true)
        setTimeout(() => {
            urlInputRef.current?.focus()
        }, 100)
    }

    const hideUrlDialog = () => {
        setUrlDialogVisible(false)
        setUrlError(null)
    }

    const handleUrlSubmit = () => {
        // Validate URL
        const urlValidation = validation.checkUrl()
        const isValid = urlValidation.validate?.(urlInput) ?? true

        if (isValid) {
            setUrl(urlInput)
            hideUrlDialog()
        } else {
            setUrlError("URL invalide")
        }
    }

    const handleUrlCancel = () => {
        setUrlInput("")
        hideUrlDialog()
    }

    const handlePickGallery = async () => {
        setPickingImage(true)
        try {
            if (multiple) {
                // Multiple mode: use expo-image-picker directly for multiple selection
                const result = await ImagePicker.launchImageLibraryAsync({
                    mediaTypes: ["images"],
                    quality: 1,
                    allowsMultipleSelection: true,
                })

                if (!result.canceled && result.assets?.length > 0) {
                    const newImages = result.assets.map((asset) => ({
                        uri: asset.uri,
                        width: asset.width,
                        height: asset.height,
                        selected: false,
                    }))
                    const imagesCopy = images?.map((x) => x) ?? []
                    imagesCopy.push(...newImages)
                    setImages(imagesCopy)
                }
            } else {
                // Single mode: replace existing image with new one
                const result = await ImagePicker.launchImageLibraryAsync({
                    mediaTypes: ["images"],
                    quality: 1,
                })

                if (!result.canceled && result.assets?.[0]) {
                    const newImage = {
                        uri: result.assets[0].uri,
                        width: result.assets[0].width,
                        height: result.assets[0].height,
                        selected: false,
                    }
                    setImages([newImage])
                }
            }
        } catch (error) {
            console.error("Error picking images:", error)
        } finally {
            setPickingImage(false)
        }
    }

    const handlePickDocument = () => {
        setPickingImage(true)
        documentPicker()
            .then((file) => {
                if (!file) return
                const { uri } = file
                if (isPdfFile(file)) {
                    // PDFs are always single (even in multiple mode)
                    setPdf(uri)
                    setImages([])
                } else {
                    const newImage = { uri }
                    if (multiple) {
                        // Multiple mode: add to array
                        const imagesCopy = [...(images || [])]
                        imagesCopy.push(newImage)
                        setImages(imagesCopy)
                    } else {
                        // Single mode: replace array with single item
                        setImages([newImage])
                    }
                }
            })
            .finally(() => setPickingImage(false))
    }

    const renderButton = ({ onPress = null, addMore = false } = {}) =>
        pickingImage && images?.length === 0 ? (
            <ActivityIndicator
                size="small"
                style={{ marginVertical: 30 }}
                color={theme.colors.primary}
            />
        ) : (
            <TouchableOpacity
                disabled={!onPress}
                onPress={onPress}
                style={{
                    marginHorizontal: 20,
                    paddingVertical: 15,
                    paddingHorizontal: 20,
                    borderRadius: theme.roundness,
                    alignSelf: "center",
                    width:
                        IS_WEB || dimensions?.width > 800
                            ? (dimensions?.width * 3) / 5
                            : "100%",
                    marginBottom: 5,
                    borderWidth: 1,
                    borderColor: theme.colors.light,
                    justifyContent: "center",
                    flexDirection: "row",
                    ...(addMore ? addMoreButtonStyle : buttonStyle),
                }}
            >
                <FontAwesome5
                    name={"photo-video"}
                    size={16}
                    color={theme.colors.primary}
                />
                <TextM
                    wrap
                    fontWeight="ExtraBold"
                    color={theme.colors.primary}
                    fontSize={14}
                    style={{ marginLeft: 12 }}
                >
                    {(addMore ? addMoreButtonLabel : buttonLabel).toUpperCase()}
                </TextM>
            </TouchableOpacity>
        )

    const renderIconButton = (onPress) => (
        <IconButton
            icon={"trash"}
            style={{ position: "absolute", top: -10, right: -10 }}
            iconColor={theme.colors.light}
            size={24}
            onPress={onPress}
        />
    )

    // Memoize renderTitle to prevent infinite loops
    const renderTitle = useCallback(
        (title) => {
            // Only call onUrlTitleChange if title actually changed
            const truncatedTitle =
                title.substring(0, 35) + (title?.length > 35 ? "..." : "")
            if (lastTitleRef.current !== truncatedTitle && onUrlTitleChange) {
                lastTitleRef.current = truncatedTitle
                onUrlTitleChange(truncatedTitle)
            }
            return <TextM fontWeight={"Bold"}>{title}</TextM>
        },
        [onUrlTitleChange]
    )

    const renderLinkPreview = useCallback(
        (disabled = false) => {
            const previewUrl = IS_WEB
                ? API_URL + "/api/cors-proxy?url=" + url
                : url

            if (!url) return null

            return (
                <LinkPreview
                    text={previewUrl}
                    renderText={() => <TextM>{url}</TextM>}
                    renderImage={() => <></>}
                    renderTitle={renderTitle}
                    renderDescription={(description) => (
                        <TextM>{description}</TextM>
                    )}
                    touchableWithoutFeedbackProps={{
                        disabled: disabled,
                    }}
                    containerStyle={{
                        backgroundColor: theme.colors.linkLighter,
                        borderRadius: theme.roundness,
                        marginTop: 20,
                        overflow: "hidden",
                    }}
                />
            )
        },
        [url, renderTitle]
    )

    // Determine if we should show the button or the media preview
    const hasPdf =
        pdf || (images && images.length > 0 && checkIsPdf(images[0]?.uri))
    const hasImages = images && images.length > 0 && !hasPdf
    const hasUrl = url && !hasPdf && !hasImages

    const handleDeleteUrl = () => setConfirmDeleteUrlModalVisible(true)

    const renderUrlDialog = () => (
        <Portal>
            <Dialog
                visible={urlDialogVisible}
                onDismiss={hideUrlDialog}
                dismissable={false}
                style={{
                    maxWidth: dimensions?.width,
                    alignSelf: "center",
                    backgroundColor: theme.colors.white,
                    borderRadius: 20,
                    elevation: 10,
                    zIndex: 10,
                }}
            >
                <Dialog.Title>Lien externe</Dialog.Title>
                <Dialog.Content>
                    <TextInput
                        ref={urlInputRef}
                        placeholder={
                            'Exemple : "https://maths-pdf.fr/les-fonctions-affines-exercices-maths-troisieme-10"'
                        }
                        value={urlInput}
                        onChangeText={(value) => {
                            setUrlInput(value)
                            setUrlError(null)
                        }}
                        onSubmitEditing={handleUrlSubmit}
                        returnKeyType={"done"}
                        error={!!urlError}
                        mode="outlined"
                        style={{
                            width:
                                IS_WEB || dimensions?.width > 800
                                    ? (dimensions?.width * 3) / 5
                                    : "100%",
                        }}
                    />
                    {urlError && (
                        <TextM
                            color={theme.colors.error}
                            fontSize={12}
                            style={{ marginTop: 5 }}
                        >
                            {urlError}
                        </TextM>
                    )}
                    {urlInput && !urlError && renderLinkPreview()}
                </Dialog.Content>
                <Dialog.Actions>
                    {urlInput !== "" && (
                        <Button
                            style={{
                                marginRight: 5,
                            }}
                            labelStyle={{
                                fontFamily: "Montserrat-Bold",
                                flexWrap: "wrap",
                            }}
                            mode={"text"}
                            textColor={theme.colors.primary}
                            onPress={handleUrlCancel}
                        >
                            {"Supprimer"}
                        </Button>
                    )}
                    <Button
                        style={{
                            marginRight: 5,
                        }}
                        labelStyle={{
                            fontFamily: "Montserrat-Bold",
                            flexWrap: "wrap",
                        }}
                        mode={"text"}
                        textColor={theme.colors.primary}
                        onPress={handleUrlSubmit}
                    >
                        {"OK"}
                    </Button>
                </Dialog.Actions>
            </Dialog>
        </Portal>
    )

    // Build menu items based on enabled options
    const menuItems = []

    if (enableExternalLink) {
        menuItems.push({
            icon: "link",
            title: "Lien externe",
            onPress: showUrlDialog,
        })
    }

    if (enableCamera) {
        menuItems.push({
            icon: "camera",
            title: "Prendre une photo",
            onPress: launchCamera,
        })
    }

    if (enableGallery) {
        menuItems.push({
            icon: "file-image",
            title: "Ouvrir la galerie",
            onPress: handlePickGallery,
        })
    }

    if (enableDocument) {
        menuItems.push({
            icon: "file",
            title: "Choisir un document (pdf, image)",
            onPress: handlePickDocument,
        })
    }

    // Video course mode: only show external link button
    if (videoCourseMode && enableExternalLink) {
        return (
            <>
                {renderButton({ onPress: showUrlDialog })}
                {renderUrlDialog()}
            </>
        )
    }

    // Normal mode: show menu with all options
    const [menuVisible, setMenuVisible] = useState(false)

    const handleOpenMenu = () => {
        setMenuVisible(true)
        handleMenuOpen()
    }

    const handleCloseMenu = () => {
        setMenuVisible(false)
        handleMenuClose()
    }

    // Android hardware back closes the menu instead of popping the screen.
    useEffect(() => {
        if (Platform.OS !== "android" || !menuVisible) return
        const subscription = BackHandler.addEventListener(
            "hardwareBackPress",
            () => {
                handleCloseMenu()
                return true
            }
        )
        return () => subscription.remove()
    }, [menuVisible, handleCloseMenu])

    // Rendered as a pure JS Portal overlay (no React Native `<Modal>`). RN's
    // Modal presents a native UIViewController on iOS, which sits in the chain
    // between the picker call and the root view controller and blocks
    // `ImagePicker.launchImageLibraryAsync` from presenting PHPicker.
    const renderMenuOptions = () => (
        <Portal>
            <View
                style={[
                    StyleSheet.absoluteFill,
                    { zIndex: 1100, elevation: 1100 },
                ]}
            >
                <TouchableOpacity
                    activeOpacity={1}
                    style={{
                        flex: 1,
                        justifyContent: "flex-end",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                    }}
                    onPress={handleCloseMenu}
                >
                    <View
                        style={{
                            backgroundColor: theme.colors.white,
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                            paddingBottom: Math.max(insets.bottom, 20),
                            maxHeight: dimensions?.height / 2,
                            elevation: 8,
                            shadowColor: "#000",
                            shadowOffset: { width: 0, height: -2 },
                            shadowOpacity: 0.1,
                            shadowRadius: 8,
                        }}
                    >
                        <View
                            style={{
                                width: 40,
                                height: 4,
                                backgroundColor: theme.colors.light,
                                borderRadius: 2,
                                alignSelf: "center",
                                marginVertical: 10,
                            }}
                        />
                        {menuItems.map((item, index) => (
                            <TouchableOpacity
                                key={index}
                                onPress={() => {
                                    handleMenuItemPress(item.onPress)()
                                    handleCloseMenu()
                                }}
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    padding: 15,
                                    borderBottomWidth:
                                        index < menuItems.length - 1 ? 1 : 0,
                                    borderBottomColor: theme.colors.lightMore,
                                }}
                            >
                                <FontAwesome5
                                    name={item.icon}
                                    size={18}
                                    color={theme.colors.primary}
                                    style={{ marginRight: 12 }}
                                />
                                <TextM fontSize={15}>{item.title}</TextM>
                            </TouchableOpacity>
                        ))}
                    </View>
                </TouchableOpacity>
            </View>
        </Portal>
    )

    // Render media previews or button
    const renderMediaContent = () => {
        // Show URL preview
        if (hasUrl) {
            return (
                <TouchableOpacity
                    style={{
                        backgroundColor: theme.colors.primaryLighter,
                        borderRadius: theme.roundness,
                        marginTop: 20,
                        padding: 20,
                    }}
                >
                    <View
                        style={{
                            width: "100%",
                            justifyContent: "center",
                            alignItems: "stretch",
                            alignSelf: "center",
                        }}
                    >
                        <TextM
                            wrap
                            fontWeight="ExtraBold"
                            color={theme.colors.primary}
                            fontSize={18}
                            style={{ textAlign: "center" }}
                        >
                            Lien externe
                        </TextM>
                        {renderLinkPreview(true)}
                        {renderIconButton(handleDeleteUrl)}
                    </View>
                </TouchableOpacity>
            )
        }

        // Show PDF preview
        if (hasPdf) {
            const pdfFiles = pdf ? [{ uri: pdf }] : images
            return (
                <MaterialPdf
                    files={pdfFiles}
                    title={null}
                    canDeleteFile={true}
                    onClear={() => setPdf(null)}
                    renderIconButton={renderIconButton}
                    handleShowMaterialModal={onShowMaterialModal}
                />
            )
        }

        // Show images preview
        if (hasImages) {
            return (
                <>
                    <MaterialImage
                        images={images}
                        setImages={setImages}
                        renderIconButton={renderIconButton}
                        canDeleteImage={true}
                        handleShowMaterialModal={onShowMaterialModal}
                        multiple={multiple}
                    />
                    {/* In multiple mode, keep showing the button to add more images */}
                    {multiple && showAddMoreButton && (
                        <TouchableOpacity
                            onPress={handleOpenMenu}
                            style={{ marginTop: 10 }}
                        >
                            {renderButton({ addMore: true })}
                        </TouchableOpacity>
                    )}
                </>
            )
        }

        // Show button if no media
        return (
            <TouchableOpacity onPress={handleOpenMenu}>
                {renderButton()}
            </TouchableOpacity>
        )
    }

    return (
        <>
            {renderMediaContent()}
            {menuVisible && renderMenuOptions()}
            {renderUrlDialog()}
            <MaterialDisplayModal
                handleShowModal={onShowMaterialModal}
                isOpen={isMaterialOpen}
                material={{ url, attachments: attachments?.map((i) => i?.uri) }}
            />
            <DialogModal
                visible={confirmDeleteUrlModalVisible}
                onDismiss={() => setConfirmDeleteUrlModalVisible(false)}
                title="Retirer le lien ?"
                onConfirm={() => {
                    if (setUrl) setUrl("")
                }}
            />
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        lastPhoto: state.camera?.lastPhoto,
        lastPhotos: state.camera?.lastPhotos,
        lastPhotoFormId: state.camera?.formId,
    }
}

const mapDispatchToProps = {
    clearLastPhoto,
    clearPhotos,
}

export default connect(mapStateToProps, mapDispatchToProps)(MediaPickerButton)
